// Combined utilities file

// ==============================
// Accessibility Utilities
// ==============================

// Focus management
export const trapFocus = (element) => {
  const focusableElements = element.querySelectorAll(
    'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
  );
  const firstFocusableElement = focusableElements[0];
  const lastFocusableElement = focusableElements[focusableElements.length - 1];

  element.addEventListener('keydown', (e) => {
    const isTabPressed = e.key === 'Tab';
    if (!isTabPressed) return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusableElement) {
        lastFocusableElement.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusableElement) {
        firstFocusableElement.focus();
        e.preventDefault();
      }
    }
  });
};

// Screen reader announcements
export const announceToScreenReader = (message, priority = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;
  document.body.appendChild(announcement);
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

// Keyboard navigation helpers
export const handleEscapeKey = (callback) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      callback();
    }
  };
  document.addEventListener('keydown', handleKeyDown);
  return () => {
    document.removeEventListener('keydown', handleKeyDown);
  };
};

// ARIA label helpers
export const generateUniqueId = (prefix = 'id') => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

export const setAriaExpanded = (element, expanded) => {
  element.setAttribute('aria-expanded', expanded.toString());
};

export const setAriaHidden = (element, hidden) => {
  element.setAttribute('aria-hidden', hidden.toString());
};

// ==============================
// Performance Optimizations
// ==============================

// Lazy loading for images
export const lazyLoadImage = (src, placeholder = '') => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(src);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
};

// Debounce function for performance
export const debounce = (func, wait, immediate) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

// Throttle function for scroll events
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Intersection Observer for lazy loading
export const createIntersectionObserver = (callback, options = {}) => {
  const defaultOptions = {
    root: null,
    rootMargin: '50px',
    threshold: 0.1
  };
  return new IntersectionObserver(callback, {...defaultOptions, ...options});
};

// Register Service Worker
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered successfully:', registration);
      return registration;
    } catch (error) {
      console.log('Service Worker registration failed:', error);
    }
  }
};

// ==============================
// SEO Utilities
// ==============================

// Dynamic meta tag management
export const updateMetaTag = (name, content, attribute = 'name') => {
  let meta = document.querySelector(`meta[${attribute}="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

// Structured data helpers
export const addStructuredData = (data) => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

// Open Graph helpers
export const updateOpenGraph = (property, content) => {
  updateMetaTag(property, content, 'property');
};

// Twitter Card helpers
export const updateTwitterCard = (name, content) => {
  updateMetaTag(name, content, 'name');
};

// Canonical URL management
export const updateCanonicalUrl = (url) => {
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = url;
};

// Page title management
export const updatePageTitle = (title, siteName = 'The Bad Company') => {
  const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;
  document.title = fullTitle;
  updateMetaTag('og:title', fullTitle, 'property');
  updateMetaTag('twitter:title', fullTitle);
};

// Meta description management
export const updateMetaDescription = (description) => {
  updateMetaTag('description', description);
  updateMetaTag('og:description', description, 'property');
  updateMetaTag('twitter:description', description);
};

// Generate structured data for different content types
export const generateStructuredData = {
  breadcrumb: (breadcrumbs) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  }),
  
  faq: (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  }),
  
  article: (article) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "image": article.image,
    "author": {
      "@type": "Organization",
      "name": "The Bad Company"
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Bad Company",
      "logo": {
        "@type": "ImageObject",
        "url": "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751341021048-thebadcompany-lightlogo.png"
      }
    },
    "datePublished": article.publishDate,
    "dateModified": article.modifyDate || article.publishDate
  }),
  
  localBusiness: (business) => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": business.name,
    "description": business.description,
    "url": business.url,
    "telephone": business.phone,
    "email": business.email,
    "address": business.address,
    "openingHoursSpecification": business.hours,
    "priceRange": business.priceRange
  })
};

// Generate sitemap
export const generateSitemap = (pages) => {
  const urls = pages.map(page => `
    <url>
      <loc>${page.url}</loc>
      <lastmod>${page.lastmod || new Date().toISOString()}</lastmod>
      <changefreq>${page.changefreq || 'monthly'}</changefreq>
      <priority>${page.priority || '0.5'}</priority>
    </url>
  `).join('');
  
  return `<?xml version="1.0" encoding="UTF-8"?>
    <urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
      ${urls}
    </urlset>`;
};